/**
 * CompiledSqlPreview — the SQL a structured (JSON) rule compiles to.
 *
 * A JSON rule has no SQL of its own: the server compiles it for the source's
 * engine when it runs. This asks the server for that compilation
 * (POST /dq-rules/compile-sql, display only, nothing runs) so a steward can
 * read what the rule will actually do before submitting or approving it.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Code2, Loader2 } from "lucide-react";

import type { CompiledRuleSql } from "../../api/rules";

type State =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "done"; data: CompiledRuleSql }
  | { status: "error"; error: string };

export function CompiledSqlPreview({
  load,
  autoLoad = false,
  resetKey,
  preClassName,
}: {
  /** Fetch the compilation (rulesApi.compileSql for the current JSON). */
  load: () => Promise<CompiledRuleSql>;
  /** Compile straight away instead of waiting for "Show SQL". */
  autoLoad?: boolean;
  /** Changes when the JSON changes: drops a compilation of the old JSON. */
  resetKey?: string;
  preClassName?: string;
}) {
  const [state, setState] = useState<State>({ status: "idle" });
  const loadRef = useRef(load);
  loadRef.current = load;
  // A response for JSON that has since changed must not overwrite the reset.
  const generation = useRef(0);

  const run = useCallback(async () => {
    const mine = ++generation.current;
    setState({ status: "loading" });
    try {
      const data = await loadRef.current();
      if (mine === generation.current) setState({ status: "done", data });
    } catch (e) {
      if (mine === generation.current)
        setState({
          status: "error",
          error: e instanceof Error ? e.message : String(e),
        });
    }
  }, []);

  useEffect(() => {
    generation.current++;
    setState({ status: "idle" });
    if (autoLoad) void run();
  }, [resetKey, autoLoad, run]);

  const pre =
    preClassName ??
    "w-full bg-black/40 border border-white/15 rounded px-2 py-2 text-xs text-white/85 font-mono whitespace-pre-wrap overflow-x-auto";

  return (
    <div className="space-y-1.5" data-testid="compiled-sql-preview">
      <div className="flex items-center gap-2">
        <span className="text-[11px] uppercase tracking-wider text-white/50">
          SQL this rule runs
        </span>
        {state.status === "done" && (
          <span className="text-[11px] text-white/40">
            {state.data.dialect}
          </span>
        )}
        {state.status !== "loading" && (
          <button
            type="button"
            onClick={() => void run()}
            className="ml-auto inline-flex items-center gap-1 text-[11px] text-blue-300 hover:text-blue-200"
          >
            <Code2 className="w-3 h-3" />
            {state.status === "idle" ? "Show SQL" : "Refresh"}
          </button>
        )}
        {state.status === "loading" && (
          <Loader2 className="ml-auto w-3 h-3 animate-spin text-white/50" />
        )}
      </div>
      {state.status === "error" && (
        <p role="alert" className="text-xs text-red-300">
          Couldn't compile this rule: {state.error}
        </p>
      )}
      {state.status === "done" && (
        <>
          {state.data.violation_sql ? (
            <pre className={pre} aria-label="Compiled violation SQL">
              {state.data.violation_sql}
            </pre>
          ) : (
            <p className="text-xs text-white/55">
              An aggregate rule has no row-level violations; it runs as the
              count query below.
            </p>
          )}
          <details open={!state.data.violation_sql}>
            <summary className="text-[11px] text-white/50 cursor-pointer">
              Count query (total and failing rows)
            </summary>
            <pre className={pre} aria-label="Compiled count SQL">
              {state.data.count_sql}
            </pre>
          </details>
          <p className="text-[11px] text-white/45">
            Compiled on the server from the JSON. {"{schema}"} is replaced with
            the source schema when it runs.
            {state.data.dialect_note ? ` ${state.data.dialect_note}` : ""}
          </p>
        </>
      )}
    </div>
  );
}
